import { inject, Injectable, signal } from "@angular/core";
import { AssetService } from "../../../services/api/asset/asset.service";
import { GateFaultService } from "../../../services/api/gate-fault/gate-fault.service";
import { TelemetryService } from "../../../services/api/telemetry/telemetry.service";
import { ASSET_TYPES } from "../../../enums/assetTypes.enum";
import { combineLatest, concatMap, first, forkJoin, map, Observable } from "rxjs";
import { GateFaultUtilsService } from "../../../services/utils/gate-fault-utils/gate-fault-utils.service";
import { BotevoAssetCompact } from "../../../models/api/asset/botevoAsset";
import { EnrichedCustomer, AttributeKey } from "../../../models/api/asset/customer";
import { GateFault } from "../../../models/api/fault/gateFault";
import { AttributeResponse } from "../../../models/shared/telemetry";
import { DOCUMENT } from "@angular/common";
import { RealtimeAttributeResponse } from "../../../models/ws/realtimeAttributes";
import {
  AttributesSubscriptionHandle,
  AttributesWebsocketService,
} from "../../../services/websocket/attributes-websocket/attributes-websocket.service";
import {
  GateFaultSubscriptionHandle,
  GateFaultWebsocketService,
} from "../../../services/websocket/gate-fault/gate-fault-websocket.service";
export const ATTRIBUTE_KEYS = [
  "readyGateSystems",
  "openGateSystems",
  "closedGateSystems",
  "numberOfOpenServices",
  "majorGateSystems",
  "criticalGateSystems",
  "partiallyOpenGateSystems",
] as const;
@Injectable()
export class CustomersFacadeService {
  // Services needed:
  private telemetryService = inject(TelemetryService);
  private assetService = inject(AssetService);
  private gateFaultService = inject(GateFaultService);
  private gateFaultUtils = inject(GateFaultUtilsService);
  private attributesWebsocket = inject(AttributesWebsocketService);
  private gateFaultWebsocket = inject(GateFaultWebsocketService);
  private document = inject(DOCUMENT);
  private localStorage = this.document.defaultView?.localStorage;

  // State variable for customer table:
  private readonly _customers = signal<EnrichedCustomer[] | null>(null);
  readonly customers = this._customers.asReadonly();

  // State varaibles for selectedCustomer:
  private readonly _gateFaultState = signal<GateFault[] | null>(null);
  readonly gateFaultState = this._gateFaultState.asReadonly();

  private readonly _availableTotalGates = signal<string | null>(null);
  readonly availableTotalGates = this._availableTotalGates.asReadonly();

  private selectedCustomerId: string | null = null;
  private attributesSubscription?: AttributesSubscriptionHandle;
  private gateFaultSubscription?: GateFaultSubscriptionHandle;

  /**
   * Initializes the state of the CustomersFacadeService by fetching all customers and their attributes.
   * Also initializes the real-time table WebSocket connection.
   */
  public loadInitialState() {
    forkJoin({
      customers: this.fetchCustomersAndAttributes(),
    }).subscribe((res) => {
      const customers = res.customers;

      this._customers.set(customers);
      const customerIds = customers.reduce((acc: string[], customer: EnrichedCustomer) => {
        if (customer.id) {
          acc.push(customer.id);
        }
        return acc;
      }, []);

      customers.filter((customer) => customer.id).map((customer) => customer.id);

      // Initializing Realtime Table:
      this.initRealtimeTable(customerIds);
    });
  }

  /**
   * Resets the state of the CustomersFacadeService to null.
   * This is used to reset the state when the user navigates away from the Customers page.
   */
  private resetStates() {
    this._customers.set(null);
    this._gateFaultState.set(null);
    this._availableTotalGates.set(null);
  }

  /**
   * Destroys the CustomersFacadeService by closing the real-time WebSocket connections.
   * This is used when the user navigates away from the Customers page.
   */
  public destroy() {
    this.resetStates();
    this.attributesSubscription?.close();
    this.gateFaultSubscription?.close();
  }
  /**
   * Sets the selected customer by updating the gateFaultState and availableTotalGates signals.
   * Also initializes the real-time gate fault WebSocket connection.
   * @param customer customer to select
   */
  public selectCustomer(customer: EnrichedCustomer) {
    if (customer.id) {
      this.selectedCustomerId = customer.id;
      forkJoin({
        gateFaults: this.fetchGateFaults(customer.id),
        gateSystems: this.fetchCustomerGateSystems(customer.id),
      }).subscribe({
        next: (res) => {
          const gateFaults = res.gateFaults;
          this._gateFaultState.set(gateFaults);

          const availableTotalGates =
            customer.readyGateSystems?.toString() + "/" + customer.numberOfGateSystems?.toString();
          this._availableTotalGates.set(availableTotalGates);

          // Initializing Realtime
          this.initRealtimeGatefault(res.gateSystems);
        },
      });
    }
  }

  /**
   * Updates the availableTotalGates signal with the incoming readyGateSystems and numberOfGateSystems.
   * @param incomingTenantStats AttributeResponse containing the readyGateSystems and numberOfGateSystems.
   */
  private handleRealtimeStats(incomingTenantStats: AttributeResponse) {
    if (incomingTenantStats.readyGateSystems) {
      this._availableTotalGates.update((currentSignalValue: string | null) => {
        if (!currentSignalValue) return currentSignalValue;

        const totalGateSystems = currentSignalValue.split("/")[1];
        const displayValue = incomingTenantStats.readyGateSystems?.value + "/" + totalGateSystems;
        return displayValue;
      });
    }

    if (incomingTenantStats.numberOfGateSystems) {
      this._availableTotalGates.update((currentSignalValue: string | null) => {
        if (!currentSignalValue) return currentSignalValue;

        const readyGateSystems = currentSignalValue.split("/")[0];
        const displayValue = readyGateSystems + "/" + incomingTenantStats.numberOfGateSystems?.value;
        return displayValue;
      });
    }
  }

  /**
   * Initializes the real-time WebSocket connection for the customer table.
   * @param customerIds Customer IDs to listen for attributes.
   */
  private initRealtimeTable(customerIds: string[]) {
    this.attributesSubscription?.close();
    const subscription = this.attributesWebsocket.register({
      entityType: ASSET_TYPES.CUSTOMER,
      entityId: customerIds,
      keys: ATTRIBUTE_KEYS,
    });
    this.attributesSubscription = subscription;
    subscription.messages$.subscribe({
      next: (data: RealtimeAttributeResponse) => {
        if (data.data && data.entityId) {
          this.handleRealtimeTable(data.data, data.entityId.toString());
          if (data.entityId.toString() === this.selectedCustomerId) {
            this.handleRealtimeStats(data.data);
          }
        }
      },
      error: () => undefined,
    });
  }

  /**
   * Handles incoming real-time attributes for a customer and updates the customer table.
   * @param incomingAttribute AttributeResponse containing the attribute to update.
   * @param customerId ID of customer to update.
   */
  private handleRealtimeTable(incomingAttribute: AttributeResponse, customerId: string) {
    // Extract and convert all present data points
    const updates: Partial<Record<keyof AttributeResponse, number>> = {};

    for (const key in incomingAttribute) {
      const entry = incomingAttribute[key];
      if (entry && entry.value !== undefined) {
        updates[key] = entry.value as number;
      }
    }

    // If no valid data points found, return early
    if (Object.keys(updates).length === 0) {
      return;
    }
    this._customers.update((customers) => {
      if (!customers) return [];
      const customersTable: EnrichedCustomer[] = [...customers];
      const foundIndex = customersTable.findIndex((asset) => asset.id === customerId);

      if (foundIndex > -1) {
        Object.entries(updates).forEach(([key, value]) => {
          customersTable[foundIndex][key as AttributeKey] = value;
        });
      }
      return customersTable;
    });
  }

  /**
   * Initializes real-time gate faults WebSocket connection.
   * @param gateSystemIds gate system IDs to listen for
   * @param token authorization token
   */
  private initRealtimeGatefault(gateSystemIds: string[]) {
    this.gateFaultSubscription?.close();
    if (gateSystemIds.length === 0) {
      return;
    }

    const subscription = this.gateFaultWebsocket.register({
      entityType: ASSET_TYPES.GATE_SYSTEM,
      entityId: gateSystemIds,
      lang: this.localStorage?.getItem("lang"),
    });
    this.gateFaultSubscription = subscription;
    subscription.messages$.subscribe({
      next: (incomingData) => {
        if (incomingData.data) {
          this.gateFaultUtils.handleRealtimeGateFault(
            incomingData.data,
            this._gateFaultState.update.bind(this._gateFaultState)
          );
        }
      },
      error: () => undefined,
    });
  }

  /**
   * Fetches the list of gate system IDs associated with a specific customer.
   *
   * @param customerId - The ID of the customer whose gate systems are to be fetched.
   * @returns An observable that emits an array of gate system IDs.
   */
  private fetchCustomerGateSystems(customerId: string): Observable<string[]> {
    return this.assetService.getAssets(ASSET_TYPES.GATE_SYSTEM, customerId, ASSET_TYPES.CUSTOMER).pipe(
      map((res) => {
        const gateSystemIds: string[] = [];
        res.forEach((gateSystem) => {
          if (gateSystem && gateSystem.id) {
            gateSystemIds.push(gateSystem.id);
          }
        });
        return gateSystemIds;
      }),
      first()
    );
  }

  /**
   * Fetches all gate faults associated with a specific customer.
   * @param customerId - The ID of the customer whose gate faults are to be fetched.
   * @returns An observable that emits an array of gate faults.
   */
  private fetchGateFaults(customerId: string) {
    return this.gateFaultService.getGateFaults(customerId, ASSET_TYPES.CUSTOMER, true);
  }

  /**
   * Fetches all customers and their attributes.
   * @returns An observable that emits an array of customers.
   */
  private fetchCustomersAndAttributes() {
    return this.assetService.getAssets(ASSET_TYPES.CUSTOMER).pipe(
      concatMap((customers: BotevoAssetCompact[]) => {
        const customersAttribute$: Observable<EnrichedCustomer>[] = [];

        customers.forEach((customer: BotevoAssetCompact) => {
          if (customer && customer.id) {
            customersAttribute$.push(
              this.telemetryService.getAttributes(customer.id, ASSET_TYPES.CUSTOMER, ATTRIBUTE_KEYS.join(",")).pipe(
                map((res) => {
                  const tableCustomer: EnrichedCustomer = customer;
                  // const extraAttributes = res.body;
                  ATTRIBUTE_KEYS.forEach((attributeKey) => {
                    tableCustomer[attributeKey] = res?.[attributeKey] ? res[attributeKey]?.value : 0;
                  });
                  return tableCustomer;
                })
              )
            );
          }
        });
        return combineLatest(customersAttribute$);
      })
    );
  }
}
